import { useMemo } from 'react';

import { useListProvidersByUser } from 'api/resource';
import type { Site } from 'interfaces';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';
import { useIsProvider } from './useIsProvider';

export type SiteIDParam = string | string[];

export const useProviderAllSites = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const isProvider = useIsProvider();
  const { id: currentSiteID, providerSiteSelection } = useCurrentSiteStore();
  const isAllSites = enabled && isProvider && providerSiteSelection === 'all';
  const { id: userID } = useCurrentUserStore();
  const providersResult = useListProvidersByUser(isAllSites ? userID : '');

  const sites = useMemo<Site[]>(() => {
    const sitesByID = new Map<string, Site>();
    for (const provider of providersResult.data || []) {
      for (const site of provider.sites || []) {
        sitesByID.set(site.id, site);
      }
    }
    return Array.from(sitesByID.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [providersResult.data]);

  const siteIDs = useMemo(() => sites.map((site) => site.id).sort(), [sites]);
  const siteID: SiteIDParam = isAllSites ? siteIDs : currentSiteID;
  const hasSiteScope = Array.isArray(siteID) ? siteID.length > 0 : !!siteID;

  return {
    hasSiteScope,
    isAllSites,
    providersResult,
    siteID,
    siteIDs,
    sites,
  };
};
